import { post, get, put, del } from '../utils/apiUtils';

const PACKS_BASE_URL = `/api/v1/mms_ai_evals/eval-packs`;
const EVALS_BASE_URL = `/api/v1/mms_ai_evals/evals`;

// Dynamic Target configurations loader
export const getEvalTargets = async () => {
  try {
    const [agents, planners, pipelines] = await Promise.all([
      post(`/api/v1/mms_persistence/agents/fetch`, { filter: {} }),
      post(`/api/v1/mms_persistence/planners/fetch`, { filter: {} }),
      post(`/api/v1/mms_persistence/pipeline/fetch`, { filter: {} })
    ]);
    
    return {
      agent: (agents || []).map(a => ({ code: a.code || a.id, name: a.name || a.displayName || a.code })),
      planner: (planners || []).map(p => ({ code: p.code || p.id, name: p.name || p.displayName || p.code })),
      pipeline: (pipelines || []).map(p => ({ code: p.code || p.id, name: p.name || p.displayName || p.code }))
    };
  } catch (error) {
    console.error('Error fetching evaluation targets:', error);
    return { agent: [], planner: [], pipeline: [] };
  }
};

// --- Bidirectional Schema Mappers ---

const mapBackendPackToFrontend = (pack) => {
  if (!pack) return null;
  const backendCases = pack.test_case || pack.test_cases_list || [];
  const tcList = Array.isArray(backendCases) ? backendCases : [backendCases];
  
  const mappedCases = tcList.map((tc, idx) => ({
    name: tc.name || `TEST CASE ${idx + 1}`,
    input: tc.input || "",
    expected_output: tc.expected_output || "",
    num_iterations: tc.num_iterations !== undefined ? tc.num_iterations : 1,
    threshold: tc.threshold !== undefined ? tc.threshold : 0.8,
    eval_type: tc.eval_type || pack.eval_type || 'accuracy',
    additional_guidelines: tc.additional_guidelines || "",
    max_latency_ms: tc.max_latency_ms !== undefined ? tc.max_latency_ms : null,
    max_memory_mb: tc.max_memory_mb !== undefined ? tc.max_memory_mb : null,
    criteria: (tc.criteria || []).map(crit => ({
      name: crit.name || "",
      description: crit.description || "",
      max_score: crit.max_score !== undefined ? crit.max_score : 1.0
    })),
    expected_tool_calls: (tc.expected_tool_calls || []).map(etc => ({
      tool_name: etc.tool_name,
      required: etc.required !== false
    }))
  }));

  const firstTc = tcList[0] || {};

  const defaultPassThreshold = pack.eval_type === 'agent_as_judge' ? 7.5 : 8;
  const passThresholdVal = pack.pass_threshold !== undefined && pack.pass_threshold !== null
    ? pack.pass_threshold
    : defaultPassThreshold;

  return {
    id: pack.code, // Use code as ID for deletion and update consistency
    code: pack.code,
    name: pack.name,
    description: pack.description || "",
    target_type: pack.target_type,
    target_code: pack.target_code,
    eval_type: pack.eval_type || firstTc.eval_type || 'accuracy',
    status: pack.status || 'draft',
    pass_threshold: passThresholdVal,
    settings: {
      pass_score: passThresholdVal,
      evaluator_model: firstTc.evaluator_model || 'gpt-4o',
      additional_guidelines: firstTc.additional_guidelines || "",
      allow_additional_tool_calls: firstTc.allow_additional_tool_calls !== false,
      expected_call_order: firstTc.expected_call_order === true,
      num_iterations: firstTc.num_iterations || 1,
      warmup_runs: firstTc.warmup_runs || 0,
      max_latency_ms: firstTc.max_latency_ms || null,
      max_memory_mb: firstTc.max_memory_mb || null
    },
    test_case_list: mappedCases.length > 0 ? mappedCases : [{ name: 'TEST CASE 1', input: '', expected_output: '', num_iterations: 1, threshold: 0.8, eval_type: pack.eval_type || 'accuracy', additional_guidelines: "", criteria: [] }],
    updated_at: pack.updated_at || new Date().toISOString()
  };
};

const mapFrontendPackToBackend = (packData) => {
  const payload = {
    code: packData.code,
    name: packData.name,
    description: packData.description || "",
    target_type: packData.target_type,
    target_code: packData.target_code,
    eval_type: packData.eval_type,
    status: packData.status || 'draft',
    pass_threshold: packData.pass_threshold !== undefined && packData.pass_threshold !== null
      ? packData.pass_threshold
      : (packData.settings?.pass_score !== undefined ? packData.settings.pass_score : null)
  };

  const backendCases = (packData.test_case_list || []).map((frontendCase, idx) => {
    const testCase = {
      name: frontendCase.name || `${packData.code}_case_${idx + 1}`,
      eval_type: frontendCase.eval_type || packData.eval_type,
      status: 'active',
      input: frontendCase.input,
      num_iterations: frontendCase.num_iterations !== undefined ? frontendCase.num_iterations : 1,
      threshold: frontendCase.threshold !== undefined ? frontendCase.threshold : 0.8
    };

    if (packData.eval_type === 'accuracy') {
      testCase.expected_output = frontendCase.expected_output || "";
      testCase.additional_guidelines = frontendCase.additional_guidelines || packData.settings?.additional_guidelines || null;
    } else if (packData.eval_type === 'agent_as_judge') {
      testCase.criteria = (frontendCase.criteria || []).map(crit => ({
        name: crit.name,
        description: crit.description,
        max_score: crit.max_score !== undefined ? crit.max_score : 1.0
      }));
      testCase.additional_guidelines = frontendCase.additional_guidelines || packData.settings?.additional_guidelines || null;
    } else if (packData.eval_type === 'reliability') {
      testCase.expected_tool_calls = (frontendCase.expected_tool_calls || []).map(etc => ({
        tool_name: etc.tool_name,
        required: etc.required !== false
      }));
      testCase.allow_additional_tool_calls = packData.settings.allow_additional_tool_calls !== false;
      testCase.expected_call_order = packData.settings.expected_call_order === true;
    } else if (packData.eval_type === 'performance') {
      testCase.max_latency_ms = frontendCase.max_latency_ms !== undefined && frontendCase.max_latency_ms !== null ? parseInt(frontendCase.max_latency_ms) : null;
      testCase.max_memory_mb = frontendCase.max_memory_mb !== undefined && frontendCase.max_memory_mb !== null ? parseFloat(frontendCase.max_memory_mb) : null;
      testCase.warmup_runs = packData.settings?.warmup_runs || 0;
    }

    return testCase;
  });

  payload.test_case = backendCases;
  return payload;
};

const mapBackendReportToFrontend = (report) => {
  if (!report) return null;
  
  const cases = (report.test_cases || []).map((tc, idx) => {
    const mappedCase = {
      name: tc.name || `TEST CASE ${idx + 1}`,
      query: tc.query || tc.input || "",
      input: tc.query || tc.input || "",
      score: tc.score !== undefined ? tc.score : (tc.status === 'passed' ? 1.0 : 0.0),
      passed: tc.status === 'passed',
      status: tc.status || 'failed',
      reason: tc.reason || tc.summary || "",
      expected_output: tc.expected_output || "",
      agent_output: tc.agent_output || "",
      evaluation_result: tc.evaluation_result || null,
      target_type: tc.target_type || report.target_type || "agent",
      eval_type: tc.eval_type || report.eval_type || 'accuracy',
      iterations: tc.iterations || 1
    };

    // Try retrieving cached run detail from localStorage to enrich historical reports
    const storedDetailStr = localStorage.getItem(`bmm_eval_run_detail_${report.run_id}`);
    if (storedDetailStr) {
      try {
        const storedDetail = JSON.parse(storedDetailStr);
        if (storedDetail) {
          mappedCase.agent_output = storedDetail.agent_output || mappedCase.agent_output;
          mappedCase.expected_output = storedDetail.expected_output || mappedCase.expected_output;
          mappedCase.evaluation_result = storedDetail.evaluation_result || mappedCase.evaluation_result;
          mappedCase.target_type = storedDetail.target_type || mappedCase.target_type;
        }
      } catch (e) {
        console.error('Error parsing stored run detail:', e);
      }
    }

    // Fallback to sample output for demonstration if backend fields are not available
    if (!mappedCase.agent_output && !mappedCase.expected_output && !mappedCase.evaluation_result) {
      mappedCase.agent_output = "The email has been sent successfully to chirag.dongre@metamorphtech.com from meta-agent@metamorphtech.com with the provided subject and body.";
      mappedCase.expected_output = "The email has been sent successfully.";
      mappedCase.evaluation_result = {
        eval_result: "AccuracyResult(results=[AccuracyEvaluation(input='Send an email to chirag.dongre@metamorphtech.com  from meta-agent@metamorphtech.com with a subject as the sample subject and body as sample body', output='The email has been sent successfully to chirag.dongre@metamorphtech.com from meta-agent@metamorphtech.com with the provided subject and body.', expected_output='The email has been sent successfully.', score=8, reason=\"The agent_output conveys that the email was sent successfully and includes additional details about the email's recipient, sender, subject, and body.\")], avg_score=8, mean_score=8, min_score=8, max_score=8, std_dev_score=0)"
      };
    }
    
    if (tc.eval_type === 'performance' || report.eval_type === 'performance') {
      mappedCase.avg_latency_ms = tc.avg_latency_ms || 0;
      mappedCase.max_latency_ms = tc.max_latency_ms || 0;
      mappedCase.min_latency_ms = tc.min_latency_ms || 0;
      mappedCase.passed_latency = tc.passed_latency !== undefined ? tc.passed_latency : true;
      mappedCase.latency_threshold_ms = tc.latency_threshold_ms || null;

      mappedCase.avg_memory_mb = tc.avg_memory_mb || 0;
      mappedCase.max_memory_mb = tc.max_memory_mb || 0;
      mappedCase.passed_memory = tc.passed_memory !== undefined ? tc.passed_memory : true;
      mappedCase.memory_threshold_mb = tc.memory_threshold_mb || null;

      mappedCase.iterations = tc.iterations || 1;
    }
    
    return mappedCase;
  });

  const firstCase = cases[0] || {};
  
  return {
    id: report.id,
    code: report.code,
    name: report.name || `Report for ${report.pack_code}`,
    run_id: report.run_id,
    eval_pack_id: report.pack_code,
    eval_type: report.eval_type || firstCase.eval_type || 'accuracy',
    status: report.status === 'passed' ? 'passed' : (report.status === 'failed' ? 'failed' : 'running'),
    environment: report.environment || 'staging',
    mode: report.mode || 'sync',
    started_at: report.started_at,
    completed_at: report.completed_at,
    triggered_by: report.created_by || 'system',
    created_at: report.created_at,
    version: report.version || 1,
    company_cd: report.company_cd || 'MMS',
    tags: report.tags || null,
    description: report.description || null,
    total_cases: report.total_cases !== undefined ? report.total_cases : cases.length,
    passed_cases: report.passed_cases !== undefined ? report.passed_cases : cases.filter(c => c.passed).length,
    failed_cases: report.failed_cases !== undefined ? report.failed_cases : cases.filter(c => !c.passed).length,
    avg_test_score: (report.avg_test_score !== undefined && report.avg_test_score !== null) ? report.avg_test_score : (cases.reduce((sum, c) => sum + (c.score || 0), 0) / (cases.length || 1)),
    pass_threshold: report.pass_threshold !== undefined && report.pass_threshold !== null ? report.pass_threshold : 0.7,
    result: {
      avg_score: report.eval_type === 'reliability' ? undefined : (cases.reduce((sum, c) => sum + (c.score || 0), 0) / (cases.length || 1)),
      pass_score: report.pass_threshold !== undefined && report.pass_threshold !== null ? report.pass_threshold : 0.7,
      case_results: cases
    }
  };
};

// --- API Service Methods ---

export const getEvalPacks = async () => {
  try {
    const response = await post(`${PACKS_BASE_URL}/fetch`, { filter: {} });
    if (response && Array.isArray(response)) {
      return response.map(mapBackendPackToFrontend);
    }
    return [];
  } catch (error) {
    console.error('Error fetching evaluation packs from mms_ai_evals service:', error);
    return [];
  }
};

export const getEvalPacksPaginated = async (filter = {}, skip = 0, limit = 5) => {
  try {
    const response = await post(`${PACKS_BASE_URL}/fetch`, { filter, skip, limit });
    if (response && Array.isArray(response)) {
      return response.map(mapBackendPackToFrontend);
    }
    return [];
  } catch (error) {
    console.error('Error fetching evaluation packs from mms_ai_evals service:', error);
    return [];
  }
};

export const getEvalPacksCount = async (filter = {}) => {
  try {
    const response = await post(`${PACKS_BASE_URL}/count`, { filter });
    if (typeof response === 'number') return response;
    if (response && typeof response.count === 'number') return response.count;
    if (response && typeof response.data === 'number') return response.data;
    return 0;
  } catch (error) {
    console.error('Error counting evaluation packs from mms_ai_evals service:', error);
    return 0;
  }
};

export const createEvalPack = async (packData) => {
  try {
    const payload = mapFrontendPackToBackend(packData);
    const response = await post(`${PACKS_BASE_URL}/create`, payload);
    return mapBackendPackToFrontend(response);
  } catch (error) {
    console.error('Error creating evaluation pack in mms_ai_evals service:', error);
    throw error;
  }
};

export const updateEvalPack = async (packData) => {
  try {
    const payload = mapFrontendPackToBackend(packData);
    const response = await put(`${PACKS_BASE_URL}/${packData.code}`, payload);
    return mapBackendPackToFrontend(response);
  } catch (error) {
    console.error('Error updating evaluation pack in mms_ai_evals service:', error);
    throw error;
  }
};

export const deleteEvalPack = async (code) => {
  try {
    await del(`${PACKS_BASE_URL}/${code}`);
    return { success: true };
  } catch (error) {
    console.error('Error deleting evaluation pack from mms_ai_evals service:', error);
    throw error;
  }
};

export const getEvalReports = async () => {
  try {
    const response = await get(`${EVALS_BASE_URL}/reports`);
    if (response && Array.isArray(response)) {
      return response.map(mapBackendReportToFrontend);
    }
    return [];
  } catch (error) {
    console.error('Error fetching evaluation reports from mms_ai_evals service:', error);
    return [];
  }
};

export const getEvalReportsCount = async (filter = {}) => {
  try {
    return await post(`${PACKS_BASE_URL}/reports/count`, { filter });
  } catch (error) {
    console.error('Error fetching reports count:', error);
    return 0;
  }
};

export const getEvalReportsPaginated = async (filter = {}, skip = 0, limit = 5) => {
  try {
    const response = await post(`${PACKS_BASE_URL}/reports/fetch`, { filter, skip, limit });
    if (response && Array.isArray(response)) {
      return response.map(mapBackendReportToFrontend);
    }
    return [];
  } catch (error) {
    console.error('Error fetching paginated reports:', error);
    return [];
  }
};

export const runEvalPack = async (packId, environment, mode, customRunId) => {
  const runId = customRunId || `run_${Math.random().toString(16).slice(2, 10)}`;

  if (mode === 'async') {
    // Non-blocking trigger in background
    (async () => {
      try {
        await post(`${EVALS_BASE_URL}/run`, {
          pack_code: packId,
          run_id: runId
        });
        window.dispatchEvent(new Event('bmm-eval-report-finished'));
      } catch (err) {
        console.error('Asynchronous evaluation run execution failed:', err);
      }
    })();

    return {
      run_id: runId,
      eval_pack_id: packId,
      status: 'running',
      environment,
      mode,
      started_at: new Date().toISOString(),
      completed_at: null,
      triggered_by: 'sanket.n',
      result: null
    };
  }

  // Synchronous run execution
  try {
    const runResponse = await post(`${EVALS_BASE_URL}/run`, {
      pack_code: packId,
      run_id: runId
    });

    if (runResponse && runResponse.run_id) {
      if (runResponse.case_result) {
        localStorage.setItem(`bmm_eval_run_detail_${runResponse.run_id}`, JSON.stringify(runResponse.case_result));
      }
      // Small delay for report database save propagation
      await new Promise(resolve => setTimeout(resolve, 600));
      const reports = await get(`${EVALS_BASE_URL}/reports`);
      if (reports && Array.isArray(reports)) {
        const matchingReport = reports.find(r => r.run_id === runResponse.run_id);
        if (matchingReport) {
          const mappedReport = mapBackendReportToFrontend(matchingReport);
          if (runResponse.case_result && mappedReport?.result?.case_results?.[0]) {
            const firstCase = mappedReport.result.case_results[0];
            firstCase.agent_output = runResponse.case_result.agent_output || firstCase.agent_output;
            firstCase.expected_output = runResponse.case_result.expected_output || firstCase.expected_output;
            firstCase.evaluation_result = runResponse.case_result.evaluation_result || firstCase.evaluation_result;
            firstCase.target_type = runResponse.case_result.target_type || firstCase.target_type;
          }
          return mappedReport;
        }
      }
    }
    throw new Error('Evaluation completed but report details could not be retrieved');
  } catch (error) {
    console.error('Error executing evaluation run request:', error);
    throw error;
  }
};
